import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ChevronDown, Menu, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/generals/button'
import { MarkSmall } from '@/components/generals/logos'
import { navLinks, pagesMenu } from '@/constants/site'
import { gsap, EASE, registerGsap, prefersReducedMotion, useIsomorphicLayoutEffect } from '@/lib/motion'

const LINK_CLASS = 'text-[15px] font-medium tracking-[-0.01em] text-[#fcfcfa] transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:text-white/60'

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)
    const [pagesOpen, setPagesOpen] = useState(false)

    const shellRef = useRef<HTMLDivElement | null>(null)
    const panelRef = useRef<HTMLDivElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    // Entrance: the bar drops in just ahead of the hero copy.
    useIsomorphicLayoutEffect(() => {
        const node = shellRef.current

        if (!node || prefersReducedMotion()) {
            return
        }

        registerGsap()

        const context = gsap.context(() => {
            gsap.fromTo(
                node,
                { y: -28, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.9,
                    ease: EASE.enter,
                    delay: 0.05,
                    clearProps: 'transform'
                }
            )
        }, node)

        return () => context.revert()
    }, [])

    // Mobile panel: the pill itself grows downward, then the links stagger in.
    useIsomorphicLayoutEffect(() => {
        const panel = panelRef.current

        if (!panel) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(panel, {
                display: menuOpen ? 'block' : 'none',
                height: menuOpen ? 'auto' : 0,
                opacity: menuOpen ? 1 : 0
            })

            return
        }

        registerGsap()

        const items = panel.querySelectorAll('[data-menu-item]')

        const context = gsap.context(() => {
            if (menuOpen) {
                gsap.set(panel, { display: 'block' })
                gsap.to(panel, {
                    height: 'auto',
                    opacity: 1,
                    duration: 0.5,
                    ease: EASE.enter
                })
                gsap.fromTo(
                    items,
                    { opacity: 0, y: 12, filter: 'blur(6px)' },
                    {
                        opacity: 1,
                        y: 0,
                        filter: 'blur(0px)',
                        duration: 0.5,
                        stagger: 0.05,
                        delay: 0.08,
                        ease: EASE.enter,
                        clearProps: 'filter'
                    }
                )

                return
            }

            gsap.to(panel, {
                height: 0,
                opacity: 0,
                duration: 0.35,
                ease: EASE.enter,
                onComplete: () => {
                    gsap.set(panel, { display: 'none' })
                }
            })
        }, panel)

        return () => context.revert()
    }, [menuOpen])

    // Desktop "Pages" flyout.
    useIsomorphicLayoutEffect(() => {
        const node = dropdownRef.current

        if (!node) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(node, {
                display: pagesOpen ? 'block' : 'none',
                opacity: pagesOpen ? 1 : 0
            })

            return
        }

        registerGsap()

        const context = gsap.context(() => {
            if (pagesOpen) {
                gsap.set(node, { display: 'block' })
                gsap.fromTo(
                    node,
                    { opacity: 0, y: -8, scale: 0.97, filter: 'blur(6px)' },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: 'blur(0px)',
                        duration: 0.35,
                        ease: EASE.enter,
                        clearProps: 'filter'
                    }
                )

                return
            }

            gsap.to(node, {
                opacity: 0,
                y: -6,
                scale: 0.98,
                duration: 0.2,
                ease: EASE.enter,
                onComplete: () => {
                    gsap.set(node, { display: 'none' })
                }
            })
        }, node)

        return () => context.revert()
    }, [pagesOpen])

    // Close everything when the route changes.
    useEffect(() => {
        const close = () => {
            setMenuOpen(false)
            setPagesOpen(false)
        }

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    // Escape closes the mobile panel and the flyout.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMenuOpen(false)
                setPagesOpen(false)
            }
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    const isActive = (href: string): boolean => {
        if (href.startsWith('/#')) {
            return false
        }

        return router.pathname === href
    }

    return (
        <div
            ref={shellRef}
            className='fixed top-0 left-0 z-50 flex w-full justify-center px-6 py-4 lg:px-16 lg:py-6'
        >
            <div
                className={cn(
                    'w-full max-w-[440px] rounded-xl bg-black/56 ring-1 ring-white/10 backdrop-blur-[10px] lg:w-auto lg:max-w-[1200px]',
                    'transition-shadow duration-300 ease-[cubic-bezier(0.44,0,0.56,1)]'
                )}
            >
                <nav
                    aria-label='Main'
                    className='flex items-center justify-between gap-4 px-4 py-3 lg:gap-4 lg:px-6'
                >
                    <Link
                        href='/'
                        aria-label='Funnel, back to home'
                        className='flex shrink-0 items-center gap-2 text-white transition-opacity duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:opacity-70'
                    >
                        <MarkSmall className='size-7' />
                    </Link>

                    <div className='hidden items-center gap-6 lg:flex'>
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(LINK_CLASS, isActive(link.href) && 'text-white/60')}
                            >
                                {link.label}
                            </Link>
                        ))}

                        <div
                            className='relative'
                            onMouseEnter={() => setPagesOpen(true)}
                            onMouseLeave={() => setPagesOpen(false)}
                        >
                            <button
                                type='button'
                                aria-expanded={pagesOpen}
                                aria-haspopup='true'
                                onClick={() => setPagesOpen((open) => !open)}
                                className={cn(LINK_CLASS, 'flex cursor-pointer items-center gap-1.5')}
                            >
                                Pages

                                <ChevronDown
                                    aria-hidden='true'
                                    className={cn(
                                        'size-4 transition-transform duration-300 ease-[cubic-bezier(0.44,0,0.56,1)]',
                                        pagesOpen && 'rotate-180'
                                    )}
                                />
                            </button>

                            <div
                                ref={dropdownRef}
                                style={{ display: 'none' }}
                                className='absolute top-full left-1/2 z-50 w-52 -translate-x-1/2 pt-4'
                            >
                                <div className='flex flex-col gap-1 rounded-xl bg-black/80 p-2 ring-1 ring-white/10 backdrop-blur-[10px]'>
                                    {pagesMenu.map((page) => (
                                        <Link
                                            key={page.href}
                                            href={page.href}
                                            className='rounded-lg px-3 py-2 text-[15px] font-medium tracking-[-0.01em] text-white/56 transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:bg-white/5 hover:text-white'
                                        >
                                            {page.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        <Button
                            href='/contact'
                            size='small'
                            className='hidden lg:inline-flex'
                        >
                            Book a call
                        </Button>

                        <button
                            type='button'
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            onClick={() => setMenuOpen((open) => !open)}
                            className='flex size-9 cursor-pointer items-center justify-center rounded-lg text-white ring-1 ring-white/10 transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:bg-white/5 lg:hidden'
                        >
                            {menuOpen ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </nav>

                <div
                    id='mobile-menu'
                    ref={panelRef}
                    style={{ display: 'none', height: 0, opacity: 0 }}
                    className='overflow-hidden lg:hidden'
                >
                    <div className='flex flex-col gap-1 px-4 pt-1 pb-5'>
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                data-menu-item=''
                                className='rounded-lg px-2 py-2.5 text-base font-medium tracking-[-0.01em] text-[#fcfcfa] transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:bg-white/5'
                            >
                                {link.label}
                            </Link>
                        ))}

                        <span
                            data-menu-item=''
                            className='mt-2 px-2 text-xs font-medium tracking-[0.08em] text-white/40 uppercase'
                        >
                            More
                        </span>

                        {pagesMenu.map((page) => (
                            <Link
                                key={page.href}
                                href={page.href}
                                data-menu-item=''
                                className='rounded-lg px-2 py-2 text-[15px] font-medium tracking-[-0.01em] text-white/56 transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:bg-white/5 hover:text-white'
                            >
                                {page.label}
                            </Link>
                        ))}

                        <div
                            data-menu-item=''
                            className='mt-3'
                        >
                            <Button
                                href='/contact'
                                className='w-full'
                            >
                                Book a call
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
